import { useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { ApiError } from '../../../../lib/api';
import { firebaseAuth } from '../../../../lib/firebase';
import { countsSummary, listErrorMessage } from '../../../../lib/portalAssets/gridView';
import {
  accountRangeIsBackwards,
  clientAssetsParams,
  EMPTY_ACCOUNT_ASSETS_FORM,
  hasAnyAccountFilter,
  type AccountAssetsForm,
} from '../../../../lib/portalAssets/accountAssetsView';
import {
  assetDetailHref,
  assetsListHref,
  assetsUploadHref,
  emptyListSentence,
  operationalAccounts,
  parseStatusFilter,
  type AssetAccountOption,
} from '../../../../lib/portalAssets/pageState';
import { narrowMode } from '../../../../lib/portalAssets/sortSearchView';
import { useDebouncedValue } from '../../../../lib/useDebouncedValue';
import type { PortalAssetTile } from '../../../../types/portalAsset';
import { AssetFilterBar, AssetGrid, BulkActionBar, FilterSelect, useBulkSelection, useImageErrorRefetch } from '../../../portalAssets';
import { fetchPortalMe } from '../portalApi';
import { PortalShell } from '../PortalShell';
import { portalAssetsApi, portalBulkApi } from './portalAssetsApi';

// /portal/assets — the club's photo and video library (spec §8, commit 12).
//
// ⚠️ LINKED FROM THE NAV for every portal user ('Asset management', live since
// 98c1fb05). The `assets` permission still gates the API: a user without it is
// shown the not-switched-on page rather than an error.
//
// ⚠️ Nothing here decides what the viewer may see. The scope is resolved
// server-side from the session; `?account=` only SELECTS within it, and one
// outside it comes back 404, which this page states plainly.

export type AssetsBodyState =
  | { kind: 'loading' }
  | { kind: 'noPermission' }
  | { kind: 'error'; status: number | null }
  | { kind: 'ready'; tiles: PortalAssetTile[]; filtered: boolean };

/** Presentational, so the empty / refused / unavailable states render in an SSR test. */
export function PortalAssetsBody({
  state,
  accountName,
  onImageError,
  onRetry,
  selection,
}: {
  state: AssetsBodyState;
  accountName?: (accountId: string) => string | null;
  onImageError?: () => void;
  onRetry?: () => void;
  /** Run F: the club's bulk selection. Absent in the SSR tests, which render the states alone. */
  selection?: { isSelected: (id: string) => boolean; onToggle: (id: string) => void };
}) {
  if (state.kind === 'loading') return <CenteredSpinner />;
  if (state.kind === 'noPermission' || state.kind === 'error') {
    const { message, retry } = listErrorMessage(state.kind === 'noPermission' ? 403 : state.status);
    return (
      <div className="max-w-md" data-testid="assets-refused">
        <p className="text-sm text-muted-foreground">{message}</p>
        {retry && onRetry && (
          <Button variant="outline" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  if (state.tiles.length === 0) {
    return (
      <p className="max-w-prose text-sm text-muted-foreground" data-testid="assets-empty">
        {emptyListSentence(state.filtered)}
      </p>
    );
  }
  return <AssetGrid tiles={state.tiles} hrefFor={(t) => assetDetailHref(t.id)} onImageError={onImageError} accountName={accountName} selection={selection} />;
}

export function PortalAssetsPage() {
  const [params, setParams] = useSearchParams();
  const account = params.get('account') ?? undefined;

  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  const permitted = me.data ? me.data.permissions.includes('assets') : null;
  const options: AssetAccountOption[] = me.data ? operationalAccounts(me.data.accounts) : [];

  // The club's filters, search and sort (Run C): the SAME form, query rules and bar as the staff pages. `?status=` is still
  // honoured as the INITIAL status (an old link), then lives in the form.
  const [form, setForm] = useState<AccountAssetsForm>(() => ({
    ...EMPTY_ACCOUNT_ASSETS_FORM,
    status: parseStatusFilter(params.get('status')) ?? '',
  }));
  const debouncedSearch = useDebouncedValue(form.search, 300);
  const backwards = narrowMode(form) === null && accountRangeIsBackwards(form);
  // ⚠️ clientAssetsParams makes the Kyck-review filter impossible to send; the client list route would 400 it.
  const listParams = clientAssetsParams({ ...form, search: debouncedSearch }, account);
  const filtered = hasAnyAccountFilter(form);

  // ⚠️ Categories belong to a BRAND and the endpoint derives the brand from ONE account, so the category filter is offered
  // only when a single location is in play (picked, or the only one the club has). Category TYPE needs no account and is
  // always offered. A club spanning brands picks a location first, which is when its categories are knowable.
  const categoryAccount = account ?? (options.length === 1 ? options[0]!.id : undefined);
  const categories = useQuery({
    queryKey: ['portal-assets', 'categories', categoryAccount ?? null],
    queryFn: () => portalAssetsApi.categories(categoryAccount!),
    enabled: permitted === true && categoryAccount !== undefined,
    retry: false,
  });

  const list = useInfiniteQuery({
    queryKey: ['portal-assets', 'list', listParams],
    queryFn: ({ pageParam }) => portalAssetsApi.list({ ...listParams, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: permitted === true && !backwards,
    retry: false,
  });
  const counts = useQuery({
    queryKey: ['portal-assets', 'counts'],
    queryFn: () => portalAssetsApi.counts(undefined),
    enabled: permitted === true,
    retry: false,
  });
  const onImageError = useImageErrorRefetch(list.refetch);
  const qc = useQueryClient();

  const names = new Map(options.map((o) => [o.id, o.name]));
  const state: AssetsBodyState =
    me.isPending || (permitted && !backwards && list.isPending)
      ? { kind: 'loading' }
      : me.isError
        ? { kind: 'error', status: me.error instanceof ApiError ? me.error.status : null }
        : !permitted
          ? { kind: 'noPermission' }
          : list.isError
            ? { kind: 'error', status: list.error instanceof ApiError ? list.error.status : null }
            : { kind: 'ready', tiles: list.data?.pages.flatMap((p) => p.tiles) ?? [], filtered };

  // BULK (Run F). The selection is only ever what is on screen (useBulkSelection), whatever the filters then do.
  const bulk = useBulkSelection(state.kind === 'ready' ? state.tiles : []);

  const shownCounts = (counts.data?.counts ?? []).filter((c) => account === undefined || c.accountId === account);
  const summary = counts.data ? countsSummary(shownCounts) : null;

  const setAccount = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('account', value);
    else next.delete('account');
    // The old status link has done its job once the form owns the value.
    next.delete('status');
    setParams(next, { replace: true });
    // A category is one brand's: it means nothing under another location, so it is cleared rather than silently filtering to none.
    setForm((f) => ({ ...f, categoryId: '' }));
  };

  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Asset management</h1>
          <p className="mt-2 max-w-prose text-sm text-muted-foreground">
            Photos and videos you have uploaded. Kyck reviews each one before it is used.
          </p>
        </div>
        {permitted && (
          <Button asChild>
            <Link to={assetsUploadHref(account)}>Upload</Link>
          </Button>
        )}
      </div>

      {permitted && (
        <div className="mt-5 space-y-3">
          <AssetFilterBar
            form={form}
            setForm={setForm}
            categories={(categories.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
            showCategory={categoryAccount !== undefined}
            showKyckFilter={false}
            filtered={filtered}
            onClear={() => setForm((f) => ({ ...EMPTY_ACCOUNT_ASSETS_FORM, sort: f.sort }))}
            roster={
              options.length > 1 ? (
                <FilterSelect
                  label="Location"
                  value={account ?? ''}
                  onChange={setAccount}
                  options={[{ value: '', label: 'All locations' }, ...options.map((o) => ({ value: o.id, label: o.name }))]}
                />
              ) : undefined
            }
          />
          {summary && <p className="text-sm text-muted-foreground" data-testid="assets-counts">{summary.sentence}</p>}
          {backwards && (
            <p className="text-sm text-destructive" role="alert">
              The start date is after the end date.
            </p>
          )}
        </div>
      )}

      <div className="mt-6">
        {state.kind === 'ready' && state.tiles.length > 0 && (
          <div className="mb-4">
            <BulkActionBar
              tiles={bulk.selectedTiles}
              categories={categoryAccount !== undefined && categories.data ? categories.data.categories : null}
              categoryNote={
                categoryAccount === undefined
                  ? 'Choose a location to pick a category: categories belong to one brand.'
                  : "Categories couldn't be loaded, so category actions are unavailable."
              }
              onClear={bulk.clear}
              onKeepOnly={bulk.keepOnly}
              onDone={() => void qc.invalidateQueries({ queryKey: ['portal-assets'] })}
              selectNote={bulk.note}
              onSelectAll={bulk.selectAllVisible}
              api={portalBulkApi}
              audience="client"
            />
          </div>
        )}
        <PortalAssetsBody
          state={state}
          selection={{ isSelected: bulk.isSelected, onToggle: bulk.toggle }}
          accountName={options.length > 1 && account === undefined ? (id) => names.get(id) ?? null : undefined}
          onImageError={onImageError}
          onRetry={() => void list.refetch()}
        />
        {list.hasNextPage && (
          <Button variant="outline" className="mt-4" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        )}
        {state.kind === 'error' && state.status === 404 && (
          <Link to={assetsListHref()} className="mt-3 inline-block text-sm text-foreground underline">
            Show all my locations
          </Link>
        )}
      </div>
    </PortalShell>
  );
}
